/**
 * AdminClinicsPage — V1.2.0 read-then-edit (backend v0.4.0).
 *
 * Now reads from `GET /v1/admin/clinics` which returns the full admin shape
 * (mips_active, apu_id, reporting_type, submission_method, eligible_
 * clinician_count, specialty_set_codes, etc.). The old "set new values"
 * form is replaced by a proper edit modal pre-populated from server state.
 *
 * Layout per V1.2.0 plan Q3:
 *   - Inline mips_active toggle per row (single-field action — no modal)
 *   - "Edit…" button per row for everything else (multi-field — modal)
 *
 * The activation toggle PATCHes mips_active only; the backend keeps the
 * existing `mips_activated_year` semantics (auto-set on activate; cleared
 * on deactivate). Non-optimistic per the convention — the toggle disables
 * during the mutation and the table repaints from server state.
 */
import { useState } from "react";
import { Spinner } from "@tensaw/design-system";
import { SchemaDataGrid, type SchemaDataGridColumn } from "@tensaw/composition";
import { useAdminClinics } from "@/hooks/useAdminClinics";
import { usePatchClinicAdmin } from "@/hooks/usePatchClinicAdmin";
import { useSetSpecialtySets } from "@/hooks/useSetSpecialtySets";
import {
  filterCatalogForCollectionType,
  useSpecialtySets,
} from "@/hooks/useSpecialtySets";
import type { AdminClinicDetail } from "@/api/models";

// ----------------------------------------------------------------------------
// Inline activation toggle
// ----------------------------------------------------------------------------

interface ActivationToggleProps {
  clinic: AdminClinicDetail;
}

function ActivationToggle({ clinic }: ActivationToggleProps) {
  const patch = usePatchClinicAdmin();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    patch.mutate({
      clinicId: clinic.clinic_id,
      body: { mips_active: e.target.checked },
    });
  };
  return (
    <input
      type="checkbox"
      checked={clinic.mips_active}
      disabled={patch.isPending}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      aria-label={
        clinic.mips_active ? "MIPS active — click to deactivate" : "MIPS inactive — click to activate"
      }
      className="h-4 w-4 cursor-pointer accent-tw-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
    />
  );
}

// ----------------------------------------------------------------------------
// Edit modal (read-then-edit, pre-populated)
// ----------------------------------------------------------------------------

interface EditModalProps {
  clinic: AdminClinicDetail;
  onClose: () => void;
}

const REPORTING_TYPES = ["group", "individual", "apm"];

function ClinicAdminEditModal({ clinic, onClose }: EditModalProps) {
  const patch = usePatchClinicAdmin();
  const setSpecialty = useSetSpecialtySets();

  // Pre-populated from the server payload — no more "set new values" blind form
  const [activatedYear, setActivatedYear] = useState<string>(
    clinic.mips_activated_year !== null && clinic.mips_activated_year !== undefined
      ? String(clinic.mips_activated_year)
      : String(new Date().getFullYear()),
  );
  const [apuId, setApuId] = useState(clinic.apu_id ?? "");
  const [reportingType, setReportingType] = useState(clinic.reporting_type ?? "group");
  const [submissionMethod, setSubmissionMethod] = useState(clinic.submission_method ?? "MIPS CQM");
  const [eligibleClinicianCount, setEligibleClinicianCount] = useState(
    clinic.eligible_clinician_count !== null && clinic.eligible_clinician_count !== undefined
      ? String(clinic.eligible_clinician_count)
      : "",
  );

  // V1.2.1: real specialty-set configuration. Catalog drives the controlled
  // dropdown. Filtered to entries whose collection_type matches the clinic's
  // current submission method — `code` is unique within that filtered set.
  //
  // Note: the activated year and submission method are tracked separately;
  // we use the clinic's CURRENT submission_method (not the modal's edit
  // state) for the catalog filter because the dropdown should reflect what's
  // actually saved on the server, not the unsaved form state. Edge case: if
  // the AM changes submission_method in this modal AND picks specialty sets,
  // they'll save together — but the catalog they see is still the pre-edit
  // method. Documented limitation; can revisit if it bites.
  const currentYear = clinic.mips_activated_year ?? new Date().getFullYear();
  const catalogQ = useSpecialtySets({ year: currentYear });
  const filteredCatalog = filterCatalogForCollectionType(
    catalogQ.data?.specialty_sets,
    clinic.submission_method,
  );

  const [selectedCodes, setSelectedCodes] = useState<string[]>(
    () => [...(clinic.specialty_set_codes ?? [])],
  );

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const onSave = async () => {
    const ecc = eligibleClinicianCount.trim();
    const ecValue: number | null =
      ecc === "" ? null : Math.max(0, Math.floor(Number(ecc)));

    // Two sequential mutations: PATCH clinic-admin first, then PUT
    // specialty-sets. If the patch fails, we don't write specialty sets
    // either (avoids inconsistent state). If specialty-sets fails after
    // a successful patch, the modal stays open so the AM can retry —
    // the patch is already saved.
    await patch.mutateAsync({
      clinicId: clinic.clinic_id,
      body: {
        mips_activated_year: Number(activatedYear),
        apu_id: apuId.trim() === "" ? null : apuId.trim(),
        reporting_type: reportingType,
        submission_method: submissionMethod,
        eligible_clinician_count: ecValue,
      },
    });

    // Only PUT specialty sets if the selection actually changed from the
    // server value — avoids a no-op write that bumps audit timestamps.
    const serverCodes = [...(clinic.specialty_set_codes ?? [])].sort();
    const selectedSorted = [...selectedCodes].sort();
    const changed =
      serverCodes.length !== selectedSorted.length ||
      serverCodes.some((c, i) => c !== selectedSorted[i]);
    if (changed) {
      await setSpecialty.mutateAsync({
        clinicId: clinic.clinic_id,
        body: { specialty_set_codes: selectedCodes },
      });
    }

    onClose();
  };

  const saving = patch.isPending || setSpecialty.isPending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="clinic-edit-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-border px-5 py-3">
          <h3 id="clinic-edit-title" className="text-base font-semibold">
            Edit {clinic.name}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 tnum">
            clinic_id {clinic.clinic_id} · TIN {clinic.tin}
          </p>
        </div>
        <div className="space-y-3 px-5 py-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="activated-year">
              Activated year
            </label>
            <input
              id="activated-year"
              type="number"
              value={activatedYear}
              onChange={(e) => setActivatedYear(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm tnum"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="apu-id-admin">
              APU ID
            </label>
            <input
              id="apu-id-admin"
              type="text"
              value={apuId}
              onChange={(e) => setApuId(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm tnum"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="reporting-admin">
              Reporting type
            </label>
            <select
              id="reporting-admin"
              value={reportingType}
              onChange={(e) => setReportingType(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm"
            >
              {REPORTING_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="submission-admin">
              Submission method
            </label>
            <input
              id="submission-admin"
              type="text"
              value={submissionMethod}
              onChange={(e) => setSubmissionMethod(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm"
            />
          </div>
          {/* V1.2.0 — eligible clinician count drives the small-practice bonus */}
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="eligible-count">
              Eligible clinicians
            </label>
            <input
              id="eligible-count"
              type="number"
              min={0}
              value={eligibleClinicianCount}
              onChange={(e) => setEligibleClinicianCount(e.target.value)}
              placeholder="(unknown — leave blank)"
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm tnum"
            />
            <p className="mt-0.5 text-xs text-gray-400">
              ≤ 15 enables the +6 small-practice Quality bonus. Leave blank if unknown.
            </p>
          </div>
          {/* V1.2.1 — controlled multi-select dropdown backed by the v0.4.1
              specialty-sets catalog endpoint. Filtered to entries whose
              collection_type matches the clinic's submission_method so the
              `code` keys are unique within the filtered list. */}
          <div>
            <div className="block text-xs font-medium uppercase tracking-wider text-gray-500">
              Specialty sets
            </div>
            {catalogQ.isLoading ? (
              <div className="mt-1 text-xs text-gray-500">Loading catalog…</div>
            ) : catalogQ.isError ? (
              <div className="mt-1 text-xs text-red-600">
                Couldn&apos;t load specialty-set catalog. Save will skip this field.
              </div>
            ) : filteredCatalog.length === 0 ? (
              <div className="mt-1 rounded border border-border bg-gray-50 px-2 py-1.5 text-xs text-gray-500">
                No specialty sets uploaded for {currentYear} matching{" "}
                <span className="font-medium">{clinic.submission_method}</span>.
                Upload the Appendix B CSV from the Benchmarks page first.
              </div>
            ) : (
              <div className="mt-1 max-h-40 space-y-1 overflow-auto rounded border border-border bg-white px-2 py-1.5">
                {filteredCatalog.map((s) => {
                  const checked = selectedCodes.includes(s.code);
                  return (
                    <label
                      key={`${s.code}|${s.collection_type}`}
                      className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCode(s.code)}
                        className="h-4 w-4 accent-tw-teal-500"
                      />
                      <span className="font-medium text-gray-800">{s.name}</span>
                      <span className="ml-auto text-xs text-gray-500 tnum">
                        {s.measure_count} {s.measure_count === 1 ? "measure" : "measures"}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="mt-0.5 text-xs text-gray-400">
              Selecting sets enables the EMA-aware Quality denominator. Union
              of measures, capped at 6 (denominator 60).
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-button border border-border bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={saving}
            className="rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

export function AdminClinicsPage() {
  const clinicsQ = useAdminClinics();
  const [editing, setEditing] = useState<AdminClinicDetail | null>(null);

  const columns: SchemaDataGridColumn<AdminClinicDetail>[] = [
    {
      id: "active",
      header: "Active",
      width: 70,
      align: "center",
      cell: ({ row }) => <ActivationToggle clinic={row} />,
    },
    {
      id: "clinic_id",
      header: "ID",
      width: 80,
      align: "right",
      cell: ({ row }) => <span className="num-cell text-sm">{row.clinic_id}</span>,
    },
    {
      id: "name",
      header: "Name",
      minWidth: 220,
      cell: ({ row }) => (
        <div className="leading-tight">
          <div className={["text-sm font-medium", row.mips_active ? "text-gray-800" : "text-gray-400"].join(" ")}>
            {row.name}
          </div>
          {row.mips_activated_year !== null && row.mips_activated_year !== undefined ? (
            <div className="text-xs text-gray-500">Active since {row.mips_activated_year}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: "tin",
      header: "TIN",
      width: 140,
      cell: ({ row }) => <span className="num-cell text-sm tnum">{row.tin}</span>,
    },
    {
      id: "reporting",
      header: "Reporting",
      width: 110,
      cell: ({ row }) => <span className="text-sm text-gray-700">{row.reporting_type ?? "—"}</span>,
    },
    {
      id: "eligible_clinicians",
      header: "Clinicians",
      width: 100,
      align: "right",
      cell: ({ row }) =>
        row.eligible_clinician_count !== null && row.eligible_clinician_count !== undefined ? (
          <span className="num-cell text-sm tnum">{row.eligible_clinician_count}</span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      id: "specialty_sets",
      header: "Specialty sets",
      minWidth: 180,
      cell: ({ row }) => {
        const codes = row.specialty_set_codes ?? [];
        return codes.length > 0 ? (
          <span className="text-xs text-gray-600">{codes.join(", ")}</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      width: 100,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => setEditing(row)}
          className="text-sm text-tw-teal-700 hover:underline"
        >
          Edit…
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-tw-teal-400">
          Admin
        </div>
        <h1 className="text-2xl font-semibold text-gray-800">Clinic activation</h1>
        <p className="mt-1 text-sm text-gray-600">
          Activate or deactivate clinics inline. Click Edit to change clinic
          settings, eligible clinicians, or specialty sets.
        </p>
      </div>

      {clinicsQ.isLoading ? (
        <div className="flex h-32 items-center justify-center" role="status">
          <Spinner />
        </div>
      ) : clinicsQ.isError || !clinicsQ.data ? (
        <div className="rounded-lg border border-border bg-white p-6">
          <div className="text-sm text-gray-600">
            Couldn&apos;t load clinics. {clinicsQ.error instanceof Error ? clinicsQ.error.message : ""}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white">
          <SchemaDataGrid<AdminClinicDetail>
            rows={clinicsQ.data.clinics}
            columns={columns}
            getRowId={(r) => String(r.clinic_id)}
            density="compact"
            emptyTitle="No clinics"
            emptyBody="There are no clinics in this tenant yet."
          />
        </div>
      )}

      {editing ? (
        <ClinicAdminEditModal clinic={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}
